import type {
  CreatePasswordPayload,
  CreatePasswordResponse,
} from '@/domains/auth/auth.entity';
import type { AuthRepository } from '@/domains/auth/auth.repository';

/**
 * Use Case para crear o establecer una nueva contraseña en el sistema.
 *
 * Funcionalidad:
 * - Permite a los usuarios crear su contraseña inicial o restablecerla
 * - Coordina el proceso de creación de contraseña con el backend
 * - Valida y persiste la nueva contraseña del usuario
 *
 * Responsabilidades:
 * - Validar el payload de creación de contraseña
 * - Coordinar con el repositorio de autenticación para crear la contraseña
 * - Actuar como intermediario entre dominio e infraestructura
 * - Asegurar que la contraseña cumpla con las políticas de seguridad del sistema
 */
export class CreatePasswordUseCase {
  private authRepository: AuthRepository;

  /**
   * Inicializa el UseCase con las dependencias necesarias.
   *
   * @param authRepository - Repositorio que implementa AuthRepository para operaciones de autenticación
   */
  constructor(authRepository: AuthRepository) {
    this.authRepository = authRepository;
  }

  /**
   * Ejecuta la creación de una nueva contraseña para el usuario.
   *
   * @param payload - Datos necesarios para crear la contraseña (usuario, contraseña nueva, token, etc.)
   */
  async execute(
    payload: CreatePasswordPayload,
  ): Promise<CreatePasswordResponse> {
    return await this.authRepository.createPassword(payload);
  }
}
